import React, { useState, useEffect, useRef, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { v4 } from "uuid";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { Product, ProductReservation, ProductType } from "../../models/Product";
import { Warehouse } from "../../models/Warehouse";
import { RoleType } from "../../models/Role";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { activeUser } from "../../services/AuthService";
import { useApplicationContext } from "../../application/ApplicationContext";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllProducts } from "../../services/ProductService";
import {
  getAllProductReservations,
  getOneProductReservation,
} from "../../services/ProductReservationService";
import { NewProductReservationForm } from "../../forms/NewProductReservationForm/NewProductReservationForm";
import { Utility } from "../../core/Utility";
import "./ProductReproReservationPage.scss";

export const ProductReproReservationPage = () => {
  const { t } = useTranslation();
  const toast = useRef<any>(null);

  const { state, selectedDateFromSet, selectedDateToSet } =
    useApplicationContext();
  const businessYear = state.businessYear ?? new Date().getFullYear();
  const yearStart = new Date(businessYear, 0, 1);
  const yearEnd = new Date(businessYear, 11, 31);
  const getDefaultRangeForBusinessYear = () => {
    const today = new Date();
    if (businessYear === today.getFullYear()) {
      // Za tekuću godinu, postavi datume od 1. januara do danas
      const from = new Date(today.getFullYear(), 0, 1);
      const to = today;
      return { from, to };
    } else {
      // Za prethodne godine, postavi ceo opseg te godine
      return { from: yearStart, to: yearEnd };
    }
  };
  const [dateFrom, setDateFrom] = useState<Date>(
    () => getDefaultRangeForBusinessYear().from,
  );
  const [dateTo, setDateTo] = useState<Date>(
    () => getDefaultRangeForBusinessYear().to,
  );

  const [processing, setProcessing] = useState(false);
  const [warehousesApi, setWarehousesApi] = useState<Warehouse[]>([]);
  const [productsApi, setProductsApi] = useState<Product[]>([]);
  const [reservations, setReservations] = useState<ProductReservation[]>([]);

  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse>(
    activeUser?.role?.name === RoleType.Warehouseman
      ? (activeUser?.warehouse as Warehouse)
      : ({} as Warehouse),
  );
  const [selectedProductReservation, setSelectedProductReservation] =
    useState<ProductReservation>();
  const [showForm, setShowForm] = useState<string>("");

  const [filters, setFilters] = useState<DataTableFilterMeta>({
    warehouseName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    partnerName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    productNames: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(25);

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const refreshLookups = async () => {
    const [wResp, pResp] = await Promise.all([
      getAllWarehouses(),
      getAllProducts(),
    ]);
    ensureSuccess(wResp);
    ensureSuccess(pResp);

    const wh: Warehouse[] = wResp?.data?.result ?? [];
    const pr: Product[] = pResp?.data?.result ?? [];

    setWarehousesApi(wh);
    setProductsApi(pr);

    return { wh, pr };
  };

  const buildProductNamesFromJson = (jsonStr: any) => {
    if (!jsonStr) return "";

    try {
      const parsed =
        typeof jsonStr === "string" ? JSON.parse(jsonStr) : jsonStr;
      if (!Array.isArray(parsed)) return "";

      const names = parsed
        .map((p: any) => {
          const id = p?.id ?? p?.productId ?? p?.name;
          const found = productsApi.find((x) => String(x.id) === String(id));
          return found?.name ?? p?.name ?? "";
        })
        .filter(Boolean);

      return names.join(", ");
    } catch {
      return "";
    }
  };

  const refreshReservations = async () => {
    setProcessing(true);
    try {
      const resp = await getAllProductReservations({
        year: businessYear,
        dateFrom,
        dateTo,
      });
      ensureSuccess(resp);

      let list: ProductReservation[] = resp?.data?.result ?? [];

      // samo repromaterijal
      list = list.filter((r: any) => r?.type === ProductType.RawMaterial);

      if (selectedWarehouse?.id) {
        list = list.filter(
          (r: any) => String(r?.warehouse?.id) === String(selectedWarehouse.id),
        );
      }

      // komercijalista vidi svoje
      if (activeUser?.role?.name === RoleType.Commercialist) {
        const fullName =
          `${activeUser?.firstName ?? ""} ${activeUser?.lastName ?? ""}`.trim();
        list = list.filter((r: any) => r?.partner?.commercialist1 === fullName);
      }

      // normalizacija polja za PrimeReact filtere
      const normalized: any[] = list.map((r: any) => ({
        ...r,
        warehouseName: r?.warehouse?.name ?? "",
        partnerName: r?.partner?.name ?? "",
        productNames: buildProductNamesFromJson(
          r?.repromaterialProductsReservationJSON,
        ),
      }));

      setReservations(normalized as ProductReservation[]);
    } catch (e: any) {
      console.error(e);
      toast.current?.show({
        severity: "error",
        detail:
          e?.response?.data?.message ??
          String(t("Greška pri učitavanju rezervacija")),
        life: 3500,
      });
      setReservations([]);
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    const run = async () => {
      setProcessing(true);
      try {
        await refreshLookups();
      } catch (e: any) {
        console.error(e);
        toast.current?.show({
          severity: "error",
          detail:
            e?.response?.data?.message ??
            String(t("Greška pri učitavanju šifarnika")),
          life: 3500,
        });
        setWarehousesApi([]);
        setProductsApi([]);
      } finally {
        setProcessing(false);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { from, to } = getDefaultRangeForBusinessYear();
    setDateFrom(from);
    setDateTo(to);
    selectedDateFromSet(from);
    selectedDateToSet(to);
  }, [businessYear]);

  useEffect(() => {
    refreshReservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    businessYear,
    dateFrom,
    dateTo,
    selectedWarehouse?.id,
    productsApi.length,
  ]);

  const handleWarehouseChange = (_prop: string, value: any) => {
    const wh = warehousesApi.find((w) => w.id == value) as Warehouse;
    setSelectedWarehouse(wh ?? ({} as Warehouse));
  };

  const onDateFromChange = (_property: string, value: Date | null) => {
    setDateFrom(value as Date);
  };

  const onDateToChange = (_property: string, value: Date | null) => {
    setDateTo(value as Date);
  };

  const openView = async (row: ProductReservation) => {
    try {
      const id = String((row as any)?.id ?? "");
      if (!id) return;

      const resp = await getOneProductReservation(id);
      ensureSuccess(resp);

      const pr = resp?.data?.result as ProductReservation;
      setSelectedProductReservation(cloneDeep(pr));
      setShowForm(v4());
    } catch (e: any) {
      console.error(e);
      toast.current?.show({
        severity: "error",
        detail:
          e?.response?.data?.message ??
          String(t("Greška pri učitavanju rezervacije")),
        life: 3500,
      });
    }
  };

  const tableValue = useMemo(() => reservations, [reservations]);

  const {
    control,
    formState: { errors },
    setValue,
  } = useForm({
    mode: "all",
  });

  const dateBody = (row: any) =>
    row?.date ? <span>{Utility.formatDate(new Date(row.date))}</span> : <></>;

  const expiryBody = (row: any) =>
    row?.expiryDate ? (
      <span>{Utility.formatDate(new Date(row.expiryDate))}</span>
    ) : (
      <></>
    );

  const actionsBody = (row: ProductReservation) => (
    <div className="action-buttons">
      <ButtonInput
        addStyle={{ border: "none", background: "#198754" }}
        labelText={""}
        icon="bi bi-eye"
        onButtonClick={() => openView(row)}
      />
    </div>
  );

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-boxes"
              style={{ fontSize: 14, marginLeft: "1rem", marginRight: "1rem" }}
            />
            <span className="header-left-title">
              {t("Rezervacije repromaterijala")}
            </span>
            <span className="header-elements">{t("od")}</span>
            <DateInput
              id="dateFrom"
              value={dateFrom}
              onValueChange={onDateFromChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
            />
            <span className="header-elements">{t("do")}</span>
            <DateInput
              id="dateTo"
              value={dateTo}
              onValueChange={onDateToChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
            />
            <span className="header-elements">{t("u")}</span>
            <DropdownInput
              id="warehouse"
              value={selectedWarehouse?.id}
              onValueChange={handleWarehouseChange}
              optionValue="id"
              optionLabel="name"
              options={warehousesApi.filter((w) => w.virtual !== true)}
              labelText=""
              placeholderText={t("Magacin")}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={activeUser?.role?.name === RoleType.Warehouseman}
            />
          </div>
          <div className="header-right">
            <NewProductReservationForm
              buttonLabel={String(t("Kreiraj rezervaciju"))}
              buttonIcon="bi bi-boxes"
              show={showForm}
              productReservation={selectedProductReservation}
              reservationType={ProductType.RawMaterial}
              buttonStyle={{
                visibility: "hidden",
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                marginRight: "1rem",
              }}
              buttonSaveStyle={{
                border: "none",
                background: "rgba(0, 165, 83, 1)",
              }}
              buttonResetStyle={{
                border: "none",
                background: "rgba(220, 53, 69, 1)",
              }}
              refreshingData={async () => {
                setSelectedProductReservation(undefined);
                await refreshReservations();
              }}
            />
          </div>
        </div>

        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="table-body">
            <DataTable
              value={tableValue}
              dataKey="id"
              scrollable
              scrollHeight="80vh"
              stripedRows
              showGridlines
              removableSort
              sortMode="single"
              filterDisplay="row"
              filters={filters}
              onFilter={(e) => {
                setFilters(e.filters as DataTableFilterMeta);
                setFirst(0);
              }}
              paginator
              first={first}
              rows={rows}
              onPage={(e) => {
                setFirst(e.first);
                setRows(e.rows);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              currentPageReportTemplate=""
              emptyMessage={t("Nema rezervacija za zadate kriterijume")}
            >
              <Column
                field="date"
                header={t("Datum rezervacije")}
                body={dateBody}
                sortable
                style={{ minWidth: "170px" }}
              />
              <Column
                field="warehouseName"
                header={t("Magacin")}
                sortable
                filter
                style={{ minWidth: "220px" }}
              />
              <Column
                field="partnerName"
                header={t("Kupac")}
                sortable
                filter
                style={{ minWidth: "260px" }}
              />
              <Column
                field="expiryDate"
                header={t("Datum isteka")}
                body={expiryBody}
                sortable
                style={{ minWidth: "170px" }}
              />
              <Column
                field="productNames"
                header={t("Naziv robe")}
                sortable
                filter
                style={{ minWidth: "320px" }}
              />
              <Column
                header={t("Opcije")}
                body={actionsBody}
                style={{ minWidth: "140px", textAlign: "center" }}
              />
            </DataTable>
          </div>
        )}
      </div>
    </PageLayout>
  );
};
